import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  saveRoomToStorage,
  setIsProjectSwitchInitiated,
} from '@store/actionSlices/appConfig'
import { saveProjectIdentityToStorage } from '@store/actionSlices/projectIdentity'
import { removeShortlist } from '@store/actionSlices/shortlist'
import { RouteKey } from '@store/constants'
import { RootStateTypeExtra } from '@store/types'

import { useLazyGetConfigQuery } from '@api/config'

import {
  removeCacheVersion,
  setCacheVersion,
} from '@utilities/cache-version-utils'
import useDefaultRoom from '@utilities/use-default-room'

import { fetchCacheVersionFromFirebase } from '@firebaseUtil/firebase-cache-version-service'
import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { ProjectType } from '@adUtilities/constants'

import { Project, RouteContext } from '../types'

const useProjectSwitchUtils = () => {
  const dispatch = useDispatch()

  const appConfig = useSelector((state: RootStateTypeExtra) => state.appConfig)

  const projectIdentity = useSelector(
    (state: RootStateTypeExtra) => state.projectIdentity
  )
  const [getProjectConfig] = useLazyGetConfigQuery()

  const { getDefaultRoom } = useDefaultRoom()

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const triggerFirebaseProjectSwitch = useCallback(
    async ({
      projectId,
      projectLabel,
      projectName,
    }: {
      projectId: string
      projectLabel: string
      projectName: string
    }) => {
      await firebaseControlQuery
        .update({
          [`projectSwitch`]: {
            projectId,
            projectLabel,
            projectName,
          },
        })
        .then(async () => {
          await firebaseControlQuery.update({
            [`projectSwitch`]: {
              projectId: '',
              projectLabel: '',
              projectName: '',
            },
          })
        })
    },
    [firebaseControlQuery]
  )

  const getProject = useCallback(
    (project: Project) => {
      const newSessionList = projectIdentity.sessionList.map((session) => {
        if (session.projectId === project.canonicalName) {
          return {
            ...session,
            projectName: session.projectName || project.canonicalName,
            projectUUID: session.projectUUID || project.uuid,
          }
        }
        return session
      })
      const sessionProject = newSessionList.find(
        (session) => session.projectId === project.canonicalName
      )
      return { sessionProject, newSessionList }
    },
    [projectIdentity.sessionList]
  )

  const validateRedirectPath = useCallback<(path: string) => boolean>(
    (path) => {
      const routes = Object.values(RouteKey)
      return routes.includes(path as RouteKey)
    },
    []
  )

  const updateSessionWithRedirectContext = useCallback<
    (args: {
      redirectTo: string
      redirectContext?: RouteContext
      projectId: string
      sessionId: string
    }) => Promise<void>
  >(
    async ({ redirectTo, redirectContext, projectId, sessionId }) => {
      const isApartmentContext =
        redirectContext?.projectType === ProjectType.Apartment
      const isHouseAndLandContext =
        redirectContext?.projectType === ProjectType.HouseAndLand

      const data: Record<string, string> = {
        activeRoute: redirectTo,
      }

      if (isApartmentContext && redirectContext.block) {
        data['building.activeBlock'] = redirectContext.block
      }
      if (isApartmentContext && redirectContext.level) {
        data['building.activeLevel'] = redirectContext.level
      }
      if (isHouseAndLandContext && redirectContext.precinct) {
        data['houseAndLand.activePrecinctId'] = redirectContext.precinct
      }
      if (isHouseAndLandContext && redirectContext.stage) {
        data['houseAndLand.activeStageId'] = redirectContext.stage
      }

      return firebaseControlQuery.update(data, sessionId, projectId)
    },
    [firebaseControlQuery]
  )

  const processProjectSwitch = useCallback<
    (args: {
      project: Project
      redirectTo?: string
      redirectContext?: RouteContext
    }) => Promise<{
      status: boolean
      message?: string
    }>
  >(
    async ({ project, redirectTo, redirectContext }) => {
      const { sessionProject, newSessionList } = getProject(project)

      if (!sessionProject) {
        return {
          status: false,
          message: 'There are no sessions for the selected project.',
        }
      }

      const projectName = sessionProject.projectId || project.canonicalName

      removeCacheVersion()
      const version = await fetchCacheVersionFromFirebase(projectName)
      setCacheVersion(version)

      const defaultRoom = await getDefaultRoom({
        projectName,
        prioritizeCurrentRoom: true,
      })
      if (defaultRoom) {
        saveRoomToStorage(appConfig, defaultRoom)
      }

      await triggerFirebaseProjectSwitch({
        projectId: sessionProject.projectUUID || project.uuid,
        projectName,
        projectLabel: sessionProject.projectName || project.canonicalName,
      })

      saveProjectIdentityToStorage({
        ...projectIdentity,
        sessionList: newSessionList,
        projectId: project.canonicalName,
        projectName: project.canonicalName,
        sessionId: sessionProject.sessionId,
        projectUUID: project.uuid,
        projectLabel: project.name,
      })

      dispatch(removeShortlist())

      const isValidRedirectPath = redirectTo
        ? validateRedirectPath(redirectTo)
        : false

      if (redirectTo && isValidRedirectPath) {
        await updateSessionWithRedirectContext({
          redirectContext,
          redirectTo,
          projectId: sessionProject.projectId,
          sessionId: sessionProject.sessionId,
        })
        window.location.href = `/${redirectTo}`

        return {
          status: true,
        }
      }
      window.location.reload()

      return {
        status: true,
      }
    },
    [
      appConfig,
      projectIdentity,
      getProject,
      getProjectConfig,
      getDefaultRoom,
      validateRedirectPath,
      triggerFirebaseProjectSwitch,
      updateSessionWithRedirectContext,
    ]
  )

  const startProjectSwitch = useCallback<typeof processProjectSwitch>(
    async (...args) => {
      dispatch(setIsProjectSwitchInitiated(true))
      try {
        const result = await processProjectSwitch(...args)
        if (!result.status) {
          dispatch(setIsProjectSwitchInitiated(false))
        }
        return result
      } catch (error) {
        dispatch(setIsProjectSwitchInitiated(false))
        return {
          status: false,
          message: 'Failed to switch project',
        }
      }
    },
    [processProjectSwitch]
  )

  return {
    startProjectSwitch,
    validateRedirectPath,
  }
}

export default useProjectSwitchUtils
